/**
 * Single source of truth for the locales the microsite supports. Each entry describes:
 *   - `code`        BCP-47 tag used by react-intl-next (`pt-BR`, not `pt_BR`)
 *   - `nativeName`  How the locale labels itself in the dropdown
 *   - `loadMessages` Lazy import of the Traduki bundle re-exported by
 *                    this package's `./i18n/<code>` subpath
 *
 * The keys of the dynamic-import paths use the underscore form because
 * that's how Traduki names the files (see ./src/i18n/*.ts). Some supported
 * locale tags intentionally map to language-only Traduki bundles.
 */

export type LocaleCode =
	| 'en'
	| 'ja-JP'
	| 'de-DE'
	| 'fr-FR'
	| 'es'
	| 'pt-BR'
	| 'ko-KR'
	| 'zh-CN'
	| 'it-IT'
	| 'nl-NL'
	| 'pl-PL'
	| 'ru-RU';

export type LocaleMessages = Record<string, string>;

export type LocaleDescriptor = {
	code: LocaleCode;
	nativeName: string;
	loadMessages: () => Promise<LocaleMessages>;
};

const importDefault = async <T>(p: Promise<{ default: T }>): Promise<T> => (await p).default;

export const DEFAULT_LOCALE: LocaleCode = 'en';

export const SUPPORTED_LOCALES: readonly LocaleDescriptor[] = [
	{
		code: DEFAULT_LOCALE,
		nativeName: 'English',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-en" */ '../../i18n/en'),
			),
	},
	{
		code: 'ja-JP',
		nativeName: '日本語',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ja" */ '../../i18n/ja'),
			),
	},
	{
		code: 'de-DE',
		nativeName: 'Deutsch',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-de" */ '../../i18n/de'),
			),
	},
	{
		code: 'fr-FR',
		nativeName: 'Français',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-fr" */ '../../i18n/fr'),
			),
	},
	{
		code: 'es',
		nativeName: 'Español',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-es" */ '../../i18n/es'),
			),
	},
	{
		code: 'pt-BR',
		nativeName: 'Português (Brasil)',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-pt_BR" */ '../../i18n/pt_BR'),
			),
	},
	{
		code: 'ko-KR',
		nativeName: '한국어',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ko" */ '../../i18n/ko'),
			),
	},
	{
		code: 'zh-CN',
		nativeName: '简体中文',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-zh" */ '../../i18n/zh'),
			),
	},
	{
		code: 'it-IT',
		nativeName: 'Italiano',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-it" */ '../../i18n/it'),
			),
	},
	{
		code: 'nl-NL',
		nativeName: 'Nederlands',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-nl" */ '../../i18n/nl'),
			),
	},
	{
		code: 'pl-PL',
		nativeName: 'Polski',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-pl" */ '../../i18n/pl'),
			),
	},
	{
		code: 'ru-RU',
		nativeName: 'Русский',
		loadMessages: () =>
			importDefault(
				import(/* webpackChunkName: "@atlassian/twg-microsite-i18n-ru" */ '../../i18n/ru'),
			),
	},
] as const;

/** Locales offered in the footer dropdown (excludes default fallback English). */
export const SELECTABLE_LOCALES: readonly LocaleDescriptor[] = SUPPORTED_LOCALES;

export const isSupportedLocale = (value: string | null | undefined): value is LocaleCode =>
	value !== null && value !== undefined && SUPPORTED_LOCALES.some((l) => l.code === value);

export const getDescriptor = (code: LocaleCode): LocaleDescriptor =>
	// Safe non-null assert: type guard ensures `code` is in the catalog.
	SUPPORTED_LOCALES.find((l) => l.code === code) as LocaleDescriptor;
